import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { Box, Flex, TextArea, TextV2, DialogV2, IconV2, ButtonV2, InputV2, LinkV2, FormControl, InlineError } from '@withjoy/joykit';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { useTranslation } from '@shared/core';
import { getLocalStorage } from '@shared/core/storage';
import { ReactComponent as Camera } from '@assets/icons/camera2.svg';
import { useAddCashFundDialogController } from './AddCashFundDialog.controller';
import type { AddCashFundDialogProps } from './types';
import { CashFundAmount } from './components/CashFundAmount';
import { LottiePlayer } from '@shared/components/LottiePlayer';
const lottieCheckMark = 'https://withjoy.com/assets/public/apps/registry/lottie/check_animation_white.json';

import {
  StyledSpacingStackPhoto,
  StyledPhoto,
  EditPhotoButton,
  StyledDialogBody,
  HeaderNameInput,
  StyledSummary,
  CenteredFormField,
  AnimatedStyledPhoto,
  AnimatedBox,
  AnimatedDialogHeader,
  AnimatedText,
  StyledHeaderText,
  StyledCancelWrapper,
  PaymentMethodsChips,
  styles,
} from './AddCashFundDialog.styles';
import { CustomItemDataType } from '@graphql/generated-base';
import { useFilestack } from '@shared/utils/filestack';
import { CashFundPaymentMethodV2 } from './components/CashFundPaymentMethodV2';
import { ChevronLeftRounded, Close, Edit, Trash } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { AppPaymentDisclaimerDialog } from './components/CashFundPaymentMethodV2/components/AppPaymentToggle/components/AppPaymentDisclaimerDialog/AppPaymentDisclaimerDialog';
import { HoneymoonFundSuccess } from './components/HoneymoonFundSuccess';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export const DEFAULT_IMAGE = 'https://withjoy.com/assets/public/apps/registry/cash_fund_default_image.jpg';
export const DEFAULT_IMAGE_CHARITY = 'https://withjoy.com/assets/public/apps/registry/charity_fund_default_image.jpg';
const HONEYMOON_DIALOG_LAST_SHOWN_KEY = 'joyHoneymoonDialogLastShown';
const HONEYMOON_DIALOG_THROTTLE_MS = 24 * 60 * 60 * 1000;

export const DialogInternal: React.FC<AddCashFundDialogProps> = props => {
  const { eventId, donationFundInfo, fundType, isCatalogDialog, selectedCategory, addMethodType, itemPosition, registryCurrency, isOpen, hideDialog } = props;
  const { tq } = useTranslation('sharedRegistry');
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const {
    nextButton,
    addtoRegistryButton,
    nameLabel,
    descriptionLabel,
    descriptionPlaceholder,
    maxCharacters,
    giftOfText,
    giftsOfText,
    totalText,
    confirmationHeader,
    editText,
    cancelHeader,
    cancelText,
    yesText,
    noText,
    noGoalText,
    cashOrCheckText,
    enableJoyCreditText,
  } = getCashFundDialogTranslations();

  const isCharityFund = !!fundType && fundType === CustomItemDataType.charity;
  const [isHoneymoonSuccessEnabled] = useIsFeatureEnabled('enableHoneymoonCashFundSuccessModal');
  const localStorage = getLocalStorage();

  const [updatedPhoto, setUpdatedPhoto] = useState<string>(isCharityFund ? DEFAULT_IMAGE_CHARITY : DEFAULT_IMAGE);
  const [photoFilename, setPhotoFilename] = useState<string>(isCharityFund ? DEFAULT_IMAGE_CHARITY : DEFAULT_IMAGE);

  const { open: openFilepicker, getPhotoPreviewUrl } = useFilestack({
    containerId: eventId,
    transformations: {
      crop: {
        aspectRatio: 1 / 1,
        force: true,
      },
    },
    onUploadDone: async res => {
      const { filename, url } = res.filesUploaded[0];
      const photo = await getPhotoPreviewUrl({
        url,
      });

      setUpdatedPhoto(photo);
      setPhotoFilename(filename);
    },
    maxFiles: 1,
  });

  const {
    formik,
    isTheLastDialogStep,
    isTheFirstStep,
    photoAnimationProps,
    textAnimationProps,
    lastStepAnimationProps,
    lastStepHeaderAnimationProps,
    isSuccessStep,
    currentStep,
    formValues,
    amountRef,
    amountPrefixOffset,
    totalRef,
    namePlaceholderText,
    onNameFocus,
    onReviewClick,
    onCancelClick,
    handleCancel,
    handleAbortCancel,
    onAmountChange,
    onQtyChange,
    setPaymentMethod,
    onConfirmButtonClick,
    selectedPaymentMethod,
    paymentLink,
    getClickableLink,
    onAmountFocus,
    isCancelClicked,
    preventNonNumericalInput,
    NOTE_MAX_CHARS,
    isLinkMousedOver,
    handleOnMouseEnter,
    handleOnMouseLeave,
    totalGoal,
    onNameFieldKeyDown,
    onUsernameFocus,
    onDescriptionFocus,
    onQtyFocus,
    onPhotoClick,
    isLinkToCharitySelected,
    donationMethodTabs,
    isAnyAmountSelected,
    amountTabs,
    onHideGoalToggle,
    isGoalHidden,
    onHelpIconClick,
    onBackClick,
    disclaimerFormik,
    isOpenDisclaimerDialog,
    onCloseDisclaimerDialog,
    canEnableCreditCard,
    handleHoneymoonRemindMeLater,
    handleHoneymoonStartExploring,
    handleHoneymoonFundSuccessView,
  } = useAddCashFundDialogController({
    addMethodType,
    eventId,
    photoFilename,
    updatedPhoto,
    hideDialog,
    dialogShown: isOpen,
    donationFundInfo,
    openFilepicker,
    fundType,
    isCharityFund,
    isCatalogDialog,
    isNewCashFund: true,
    selectedCategory,
    itemPosition,
    registryCurrency,
    handleOnRemove: () => null,
  });
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });

  const { errors } = formik || {};
  const formikValues = formik.values;
  const descriptionLength = formikValues.description ? formikValues.description.length : 0;

  const hasShownHoneymoonDialogInLast24Hours = useMemo(() => {
    const lastShownValue = localStorage.getItem(HONEYMOON_DIALOG_LAST_SHOWN_KEY);
    const lastShownAt = lastShownValue ? Number.parseInt(lastShownValue, 10) : 0;

    return !Number.isNaN(lastShownAt) && Date.now() - lastShownAt < HONEYMOON_DIALOG_THROTTLE_MS;
  }, [localStorage]);

  const shouldShowHoneymoonSuccess = isSuccessStep && !isCharityFund && isHoneymoonSuccessEnabled && !hasShownHoneymoonDialogInLast24Hours;

  const handleHoneymoonSuccessView = useCallback(() => {
    localStorage.setItem(HONEYMOON_DIALOG_LAST_SHOWN_KEY, Date.now().toString());
    handleHoneymoonFundSuccessView();
  }, [handleHoneymoonFundSuccessView, localStorage]);

  const getConfirmButtonContent = () => {
    if (isTheLastDialogStep()) {
      return addtoRegistryButton;
    } else {
      return nextButton;
    }
  };

  const getAmountSummary = () => {
    if (isAnyAmountSelected) {
      if (formikValues.amount) {
        return <> {`${formValues.currency}${formValues.amount}`}</>;
      } else {
        return <> {noGoalText} </>;
      }
    } else {
      return (
        <>
          {`${formValues.qty} ${formValues.qty > 1 ? giftsOfText : giftOfText} ${formValues.currency}${formValues.amount}`}
          <span>{`${formValues.currency}${totalGoal} ${totalText}`}</span>
        </>
      );
    }
  };

  const renderAddCashFundDialogHeader = () => {
    if (!isTheFirstStep()) {
      return (
        <AnimatedDialogHeader
          style={lastStepHeaderAnimationProps}
          padding={['4rem 1.25rem 1.5rem', '2.5rem 2.5rem 1.5rem']}
          borderRadius={['0', '12px 12px 0 0']}
          $isSuccessStep={isSuccessStep}
        >
          <AnimatedStyledPhoto
            thumbnail={true}
            isSuccessStep={isSuccessStep}
            $backgroundImage={updatedPhoto}
            backgroundColor={'mono2'}
            style={photoAnimationProps}
            marginBottom={isSuccessStep ? 6 : 4}
            marginX="auto"
            onClick={isSuccessStep ? undefined : onPhotoClick}
          >
            {isSuccessStep ? (
              <LottiePlayer
                autoplay={true}
                lottieUrl={lottieCheckMark}
              />
            ) : (
              <StyledSpacingStackPhoto
                spacing={5}
                tabIndex={0}
              >
                <EditPhotoButton thumbnail={true}>
                  <Camera />
                </EditPhotoButton>
              </StyledSpacingStackPhoto>
            )}
          </AnimatedStyledPhoto>
          {isSuccessStep ? (
            <>
              <StyledHeaderText
                typographyVariant="hed5"
                component="p"
              >
                {`"${formikValues.name}"`}
              </StyledHeaderText>
              <AnimatedText style={textAnimationProps}>
                <StyledHeaderText
                  typographyVariant="hed5"
                  component="p"
                  marginBottom={6}
                >
                  {confirmationHeader}
                </StyledHeaderText>
              </AnimatedText>
              <Box __css={styles.closeButtonContainer}>
                <ButtonV2
                  width={{ _: '100%', md: pxToRem(144) }}
                  variant="outline"
                  intent="neutral"
                  onClick={handleCancel}
                >
                  {tq`Close`}
                </ButtonV2>
              </Box>
            </>
          ) : (
            <HeaderNameInput
              minimal={true}
              disabled={isSuccessStep}
              width="initial"
              {...formik.getFieldProps('name')}
              onFocus={onNameFocus}
            />
          )}

          {!isSuccessStep && (
            <AnimatedBox
              style={{
                opacity: lastStepAnimationProps.finalStep,
                transform: lastStepAnimationProps.finalStep.interpolate(x => `translate3d(0, ${(1 - x) * 100}%, 0)`),
              }}
            >
              {currentStep > 1 && (
                <Flex
                  alignItems="center"
                  justifyContent={['space-between', null, 'center']}
                  marginTop={2}
                >
                  <StyledSummary
                    onClick={() => onReviewClick(1)}
                    tabIndex={0}
                    useHoverState={isLinkMousedOver}
                  >
                    {getAmountSummary()}
                    <IconV2 size="sm">
                      <Edit />
                    </IconV2>
                  </StyledSummary>
                  {isMobile && (
                    <LinkV2
                      textDecoration="none"
                      typographyVariant="body1"
                      onClick={() => onReviewClick(1)}
                    >
                      {editText}
                    </LinkV2>
                  )}
                </Flex>
              )}
              {currentStep > 2 && (
                <>
                  {!isLinkToCharitySelected && (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                    >
                      <PaymentMethodsChips
                        enableAppPlatform={formValues.enableAppPlatform}
                        enableJoyCredit={formValues.enableJoyCredit}
                        enableJoyCreditText={enableJoyCreditText}
                        enableCashOrCheck={formValues.enableCashOrCheck}
                        selectedPaymentMethod={selectedPaymentMethod}
                        cashOrCheckText={cashOrCheckText}
                      />
                    </Flex>
                  )}
                  {isCharityFund && isLinkToCharitySelected && (
                    <Flex
                      alignItems="center"
                      justifyContent={['space-between', null, 'center']}
                      marginTop="9px"
                    >
                      <StyledSummary
                        onClick={() => onReviewClick(2)}
                        tabIndex={0}
                        useHoverState={isLinkMousedOver}
                      >
                        {isLinkToCharitySelected ? formValues.charityLink : `${selectedPaymentMethod?.label} @${formValues.username}`}
                        <Box as="span">
                          <Edit size={16} />
                        </Box>
                      </StyledSummary>
                      {isMobile && (
                        <LinkV2
                          textDecoration="none"
                          typographyVariant="body1"
                          onClick={() => onReviewClick(2)}
                        >
                          {editText}
                        </LinkV2>
                      )}
                    </Flex>
                  )}
                </>
              )}
              {!isMobile && currentStep > 1 && (
                <LinkV2
                  textDecoration="none"
                  marginTop={2}
                  typographyVariant="body1"
                  onClick={() => onReviewClick(currentStep - 1)}
                  tabIndex={0}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                >
                  {editText}
                </LinkV2>
              )}
            </AnimatedBox>
          )}
        </AnimatedDialogHeader>
      );
    } else return null;
  };

  const renderAddCashFundDialogContent = () => {
    switch (currentStep) {
      case 0: {
        const nameError = formik.touched.name && errors.name;
        return (
          <Box>
            <StyledPhoto
              $backgroundImage={updatedPhoto}
              backgroundColor={'mono2'}
              width="15rem"
              height="15rem"
              marginBottom={9}
              marginX="auto"
              onClick={onPhotoClick}
            >
              <StyledSpacingStackPhoto
                spacing={5}
                tabIndex={0}
              >
                <EditPhotoButton>
                  <Camera />
                </EditPhotoButton>
              </StyledSpacingStackPhoto>
            </StyledPhoto>
            <FormControl
              label={
                <TextV2
                  textAlign="center"
                  color={nameError ? 'red6' : ''}
                  typographyVariant="hed2"
                >
                  {nameLabel}
                </TextV2>
              }
              error={
                nameError ? (
                  <InlineError
                    id={nameError}
                    message={nameError}
                  />
                ) : undefined
              }
              overrides={{
                Label: {
                  props: { marginBottom: 5 },
                },
              }}
            >
              <InputV2
                {...formik.getFieldProps('name')}
                placeholder={namePlaceholderText}
                onKeyDown={onNameFieldKeyDown}
                onFocus={onNameFocus}
                tabIndex={0}
                isInvalid={!!formik.touched.name && !!errors.name}
              />
            </FormControl>
          </Box>
        );
      }
      case 1:
        return (
          <CashFundAmount
            isAnyAmountSelected={isAnyAmountSelected}
            amountTabs={amountTabs}
            formik={formik}
            isMobile={!!isMobile}
            onQtyChange={onQtyChange}
            onQtyFocus={onQtyFocus}
            preventNonNumericalInput={preventNonNumericalInput}
            onAmountChange={onAmountChange}
            onAmountFocus={onAmountFocus}
            amountRef={amountRef}
            amountPrefixOffset={amountPrefixOffset}
            totalRef={totalRef}
            totalGoal={totalGoal}
            currency={formValues.currency}
            onHideGoalToggle={onHideGoalToggle}
            isGoalHidden={isGoalHidden}
            onHelpIconClick={onHelpIconClick}
          />
        );
      case 2:
        return (
          <Flex
            flexDirection="column"
            alignItems="center"
            marginTop={[0, null, '-8px']}
          >
            <CashFundPaymentMethodV2
              isCharityFund={isCharityFund}
              isLinkToCharitySelected={isLinkToCharitySelected}
              isMobile={!!isMobile}
              donationMethodTabs={donationMethodTabs}
              formik={formik}
              setPaymentMethod={setPaymentMethod}
              selectedPaymentMethod={selectedPaymentMethod}
              paymentLink={paymentLink}
              onUsernameFocus={onUsernameFocus}
              isEditDialog={false}
              getClickableLink={getClickableLink}
              canEnableCreditCard={canEnableCreditCard}
            />
          </Flex>
        );
      case 3:
        return (
          <CenteredFormField
            label={descriptionLabel}
            error={!!errors.description}
          >
            <TextArea
              {...formik.getFieldProps('description')}
              name="description"
              placeholder={descriptionPlaceholder}
              resize={false}
              minRows={4}
              maxRows={4}
              tabIndex={0}
              error={!!errors.description}
              onFocus={onDescriptionFocus}
            />
            <TextV2
              component="p"
              typographyVariant="label2"
              color={descriptionLength > NOTE_MAX_CHARS ? 'negative6' : 'mono10'}
              textAlign="end"
            >
              {`${descriptionLength}/${NOTE_MAX_CHARS} ${maxCharacters}`}
            </TextV2>
          </CenteredFormField>
        );
      default:
        return null;
    }
  };

  const renderCancelComponent = () => {
    return isMobile ? (
      <>
        <Flex __css={styles.cancelDialogContentMobile}>
          <IconV2
            size="xxl"
            color="mono14"
            marginBottom={5}
          >
            <Trash />
          </IconV2>
          <TextV2
            component="p"
            typographyVariant="hed2"
            marginBottom={5}
          >
            {cancelHeader}
          </TextV2>
          <TextV2
            component="p"
            typographyVariant="body1"
            marginBottom={9}
          >
            {cancelText}
          </TextV2>
        </Flex>
        <Box __css={styles.cancelDialogMobileFooter}>
          <ButtonV2
            backgroundColor="buttonDestructiveFill"
            _hover={{ backgroundColor: 'buttonDestructiveHover' }}
            fullWidth={true}
            marginBottom={4}
            onClick={handleCancel}
          >
            {yesText}
          </ButtonV2>
          <ButtonV2
            variant="ghost"
            intent="neutral"
            fullWidth={true}
            onClick={handleAbortCancel}
          >
            {noText}
          </ButtonV2>
        </Box>
      </>
    ) : (
      <StyledCancelWrapper
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={['100vh', '575px']}
      >
        <IconV2
          size="xxl"
          color="mono14"
          marginBottom={5}
        >
          <Trash />
        </IconV2>
        <TextV2
          component="p"
          typographyVariant="hed5"
          marginBottom={5}
        >
          {cancelHeader}
        </TextV2>
        <TextV2
          component="p"
          typographyVariant="body2"
          marginBottom={8}
        >
          {cancelText}
        </TextV2>
        <Flex
          width="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <ButtonV2
            backgroundColor="buttonDestructiveFill"
            _hover={{ backgroundColor: 'buttonDestructiveHover' }}
            onClick={handleCancel}
            marginBottom={5}
          >
            {yesText}
          </ButtonV2>
          <ButtonV2
            variant="ghost"
            intent="neutral"
            onClick={handleAbortCancel}
          >
            {noText}
          </ButtonV2>
        </Flex>
      </StyledCancelWrapper>
    );
  };

  useEffect(() => {
    setUpdatedPhoto(isCharityFund ? DEFAULT_IMAGE_CHARITY : DEFAULT_IMAGE);
    setPhotoFilename(isCharityFund ? DEFAULT_IMAGE_CHARITY : DEFAULT_IMAGE);
  }, [isCharityFund]);

  return (
    <>
      {!isCancelClicked ? (
        <Flex __css={styles.root}>
          {shouldShowHoneymoonSuccess ? (
            <>
              <Box
                as="button"
                right={5}
                onClick={handleCancel}
                __css={styles.iconButton}
              >
                <IconV2 size="md">
                  <Close />
                </IconV2>
              </Box>
              <HoneymoonFundSuccess
                fundTitle={formikValues.name}
                onRemindLater={handleHoneymoonRemindMeLater}
                onStartExploring={handleHoneymoonStartExploring}
                onView={handleHoneymoonSuccessView}
              />
            </>
          ) : (
            <>
              {currentStep > 0 && !isSuccessStep && (
                <Box
                  as="button"
                  left={5}
                  onClick={onBackClick}
                  __css={styles.iconButton}
                >
                  <IconV2 size="md">
                    <ChevronLeftRounded />
                  </IconV2>
                </Box>
              )}
              <Box
                as="button"
                right={5}
                onClick={onCancelClick}
                __css={styles.iconButton}
              >
                <IconV2 size="md">
                  <Close />
                </IconV2>
              </Box>
              {renderAddCashFundDialogHeader()}
              {!isSuccessStep && (
                <AnimatedBox
                  style={{
                    opacity: lastStepAnimationProps.finalStep,
                    transform: lastStepAnimationProps.finalStep.interpolate(x => `translate3d(0, ${(1 - x) * 100}%, 0)`),
                  }}
                >
                  <StyledDialogBody
                    isMobile={isMobile}
                    isCharityPaymentStep={isCharityFund && currentStep === 2}
                    height={['100%', null, '100%']}
                  >
                    {renderAddCashFundDialogContent()}
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                    >
                      <ButtonV2
                        width={{ _: '100%', md: 'auto' }}
                        minWidth="9rem"
                        intent="neutral"
                        onClick={onConfirmButtonClick}
                      >
                        {getConfirmButtonContent()}
                      </ButtonV2>
                    </Flex>
                  </StyledDialogBody>
                </AnimatedBox>
              )}
            </>
          )}
        </Flex>
      ) : (
        renderCancelComponent()
      )}

      <AppPaymentDisclaimerDialog
        isOpen={isOpenDisclaimerDialog}
        paymentLink={paymentLink}
        paymentUsername={formik.values.username}
        paymentMethod={formik.values.paymentMethod}
        onClose={onCloseDisclaimerDialog}
        formik={disclaimerFormik}
        currencyCode={formik.values.currencyCode}
      />
    </>
  );
};

export const AddCashFundDialog: React.FC<AddCashFundDialogProps> = props => {
  const { isOpen } = props;

  return (
    <DialogV2
      isOpen={isOpen}
      id="AddCashFundDialog"
      size="xl"
      disableFocusLock={true}
    >
      <DialogInternal {...props} />
    </DialogV2>
  );
};

AddCashFundDialog.displayName = 'AddCashFundDialog';
